import $api from "@/zapi-frontend/shared/api/axios";
import { CACHE_TIME } from "@/zapi-frontend/shared/const/consts";
import { TDocument } from "@/zapi-frontend/shared/hooks/useDocuments";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

export type TContentType = "FULL" | "SUMMARIZED";

export type TDocumentContentPreference = {
  id: number;
  contentType: TContentType;
};

export type TDocumentWithContentPreference = TDocument & {
  contentType: TContentType;
};

const getContentPreferences = async (projectId: string, chapterId: string) => {
  const response = await $api.get<{ data: TDocumentWithContentPreference[] }>(
    `/projects/${projectId}/chapters/${chapterId}/content-preferences`
  );
  return response.data.data;
};

function updateContentPreference({
  projectId,
  chapterId,
  documentId,
  contentType
}: {
  projectId: string;
  chapterId: string;
  documentId: number;
  contentType: TContentType;
}) {
  return $api.patch(`/projects/${projectId}/chapters/${chapterId}/content-preferences`, {
    documentId,
    contentType
  });
}

export function useContentPreferences(projectId: string, chapterId: string, documents: TDocument[], enabled = true) {
  const [documentsWithPreferences, setDocumentsWithPreferences] = useState<TDocumentWithContentPreference[]>([]);
  const [isUpdatingPreference, setIsUpdatingPreference] = useState<boolean>(false);

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["content-preferences", projectId, chapterId],
    queryFn: () => getContentPreferences(projectId, chapterId),
    enabled: enabled && !!projectId && !!chapterId,
    staleTime: CACHE_TIME,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (data) {
      setDocumentsWithPreferences(data);
    }
  }, [data]);

  useEffect(() => {
    // Merge documents with their content preferences
    setDocumentsWithPreferences((prev) =>
      documents.map((doc) => {
        const existingPref = prev.find((p) => p.id === doc.id);
        return {
          ...doc,
          contentType: existingPref?.contentType || "FULL"
        };
      })
    );
  }, [documents]);

  const updatePreference = useCallback(
    async (documentId: number, contentType: TContentType) => {
      const old = documentsWithPreferences;
      setDocumentsWithPreferences((prev) => prev.map((d) => (d.id === documentId ? { ...d, contentType } : d)));

      try {
        setIsUpdatingPreference(true);
        await updateContentPreference({ projectId, chapterId, documentId, contentType });
        await refetch(); // Refresh to get updated data
      } catch (error) {
        console.error("Error updating content preference", error);
        setDocumentsWithPreferences(old);
        throw error;
      } finally {
        setIsUpdatingPreference(false);
      }
    },
    [projectId, chapterId, documentsWithPreferences, refetch]
  );

  return {
    documentsWithPreferences,
    isContentPreferencesLoading: isFetching,
    updatePreference,
    isUpdatingPreference
  };
}
